import React from "react";
import { Link } from "react-router-dom";
import SectionCard from "./SectionCard";
import { Badge } from "components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import { Trophy, BookOpen } from "lucide-react";

const InstitutionAvatar = ({ id, logoUrl, name, className = "w-12 h-12" }) => {
  const sealUrl = id ? `https://media.academic.online/institutions_images/${id}/seal.png` : null;
  const [src, setSrc] = React.useState(sealUrl || logoUrl);

  React.useEffect(() => {
    setSrc(sealUrl || logoUrl);
  }, [sealUrl, logoUrl]);

  return (
    <Avatar className={`rounded-lg bg-white border border-border/50 p-0.5 ${className}`}>
      <AvatarImage 
        src={src} 
        alt={name}
        className="object-contain"
        onError={() => {
          if (src === sealUrl && logoUrl) {
            setSrc(logoUrl);
          }
        }}
      />
      <AvatarFallback className="bg-explore/5 text-explore font-bold rounded-md">
        {name?.substring(0, 2).toUpperCase() || "UN"}
      </AvatarFallback>
    </Avatar>
  );
};

const SimilarInstitutionsCard = ({
  data,
  isLoading,
  error,
  isHidden,
  onRetry,
  onSeeMore
}) => {
  // Filter out the institution itself (where peer_score is ~1.0)
  const peers = (data || []).filter(peer => peer.peer_score < 0.999);

  return (
    <SectionCard
      title="Peer Institutions"
      description="Institutions with similar research profiles"
      isLoading={isLoading}
      error={error}
      isHidden={isHidden}
      isEmpty={!peers || peers.length === 0}
      onRetry={onRetry}
      onSeeMore={onSeeMore}
      seeMoreText="View peer institutions"
    >
      <div className="flex flex-col gap-3">
        {peers.slice(0, 5).map((peer, idx) => {
          const identifier = peer.explore_slug || peer.id;

          return (
            <div key={peer.id || idx} className="flex items-center gap-4 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors group">
              <Link to={`/explore/institution/${identifier}`} className="shrink-0">
                <InstitutionAvatar
                  id={peer.id}
                  logoUrl={peer.logo_url}
                  name={peer.name}
                  className="w-12 h-12"
                />
              </Link>
            
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              <Link to={`/explore/institution/${identifier}`} className="hover:underline">
                <h4 className="font-semibold text-sm line-clamp-1 group-hover:text-explore transition-colors">{peer.name}</h4>
              </Link>
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                {peer.h_index != null && (
                  <span className="flex items-center gap-1 font-medium text-foreground/80">
                    <Trophy className="w-3 h-3 text-explore/70" />
                    {peer.h_index} h-index
                  </span>
                )}
                {peer.publication_count != null && (
                  <span className="flex items-center gap-1">
                    <BookOpen className="w-3 h-3 text-muted-foreground/70" />
                    {peer.publication_count.toLocaleString()} pubs
                  </span>
                )}
              </div>
            </div>

            <div className="text-right shrink-0">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="bg-explore/5 text-explore border-explore/20 cursor-help">
                      {(peer.peer_score * 100).toFixed(0)}% Match
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-semibold mb-1">Similarity Match</p>
                    <p className="text-xs max-w-[200px]">Score based on overlap in research topics, publications, and impact.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      })}
      </div>
    </SectionCard>
  );
};

export default SimilarInstitutionsCard;
